import { useState } from "react";
import { ChevronDown, ChevronUp, GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { F, T, CORAL } from "../../theme/tokens";
import { fieldNote, fieldTypeMeta, FormField, FormSection } from "./types";

// Tiny stacked ▲▼ pair used for reordering sections and fields (also works on touch,
// where drag and drop does not).
function ArrowPair({ onUp, onDown, canUp, canDown, label }: { onUp: () => void; onDown: () => void; canUp: boolean; canDown: boolean; label: string }) {
  const btn = (enabled: boolean): React.CSSProperties => ({ display: "flex", background: "none", border: "none", padding: 1, cursor: enabled ? "pointer" : "default", color: enabled ? T.sub : "#d4d4d4" });
  return (
    <div style={{ display: "flex", flexDirection: "column", flexShrink: 0 }}>
      <button onClick={onUp} disabled={!canUp} aria-label={`Move ${label} up`} style={btn(canUp)}><ChevronUp size={13} /></button>
      <button onClick={onDown} disabled={!canDown} aria-label={`Move ${label} down`} style={btn(canDown)}><ChevronDown size={13} /></button>
    </div>
  );
}

function Pill({ children, bg, fg, border, onClick, title }: { children: React.ReactNode; bg: string; fg: string; border?: string; onClick?: () => void; title?: string }) {
  const style: React.CSSProperties = { display: "inline-flex", alignItems: "center", background: bg, color: fg, border: border ? `1px solid ${border}` : "none", borderRadius: 9999, padding: "3px 10px", fontSize: 11, fontWeight: 600, fontFamily: F, whiteSpace: "nowrap" };
  return onClick
    ? <button onClick={onClick} title={title} style={{ ...style, cursor: "pointer" }}>{children}</button>
    : <span style={style}>{children}</span>;
}

const iconBtn = (danger = false): React.CSSProperties => ({
  display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 28, borderRadius: 7, cursor: "pointer",
  border: `1px solid ${danger ? "#fecaca" : T.border}`, background: danger ? "#fee2e2" : T.s2, color: danger ? "#991b1b" : T.text,
});

function FieldRow({ field, index, count, onToggleRequired, onEdit, onDelete, onMove, drag }: {
  field: FormField; index: number; count: number;
  onToggleRequired: () => void; onEdit: () => void; onDelete: () => void; onMove: (to: number) => void;
  drag: { start: () => void; drop: () => void };
}) {
  const meta = fieldTypeMeta(field.type);
  const note = fieldNote(field);
  return (
    <div className="vf-field-row" draggable onDragStart={drag.start} onDragOver={(e) => e.preventDefault()} onDrop={drag.drop}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
      <span style={{ display: "flex", color: T.muted, cursor: "grab", flexShrink: 0 }} aria-hidden><GripVertical size={14} /></span>
      <ArrowPair label={field.label} canUp={index > 0} canDown={index < count - 1} onUp={() => onMove(index - 1)} onDown={() => onMove(index + 1)} />
      <div className="vf-field-label" style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 500, color: T.text, fontFamily: F, margin: 0, overflowWrap: "anywhere" }}>{field.label}</p>
        {field.helpText && <p style={{ fontSize: 11, color: T.muted, fontFamily: F, margin: "2px 0 0", overflowWrap: "anywhere" }}>{field.helpText}</p>}
      </div>
      <div className="vf-field-meta" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <Pill bg={meta.bg} fg={meta.fg}>{meta.label}</Pill>
        {note && <span style={{ fontSize: 11, color: T.muted, fontFamily: F, whiteSpace: "nowrap" }}>{note}</span>}
        {field.required
          ? <Pill bg="#fdf0f4" fg={CORAL} border="#f5c2d3" onClick={onToggleRequired} title="Required: click to make optional">Required</Pill>
          : <Pill bg={T.s2} fg={T.muted} border={T.border} onClick={onToggleRequired} title="Optional: click to make required">Optional</Pill>}
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={onEdit} aria-label={`Edit ${field.label}`} style={iconBtn()}><Pencil size={13} /></button>
          <button onClick={onDelete} aria-label={`Delete ${field.label}`} style={iconBtn(true)}><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

export function SectionCard({ section, index, count, onMoveSection, onEditSection, onDeleteSection, onAddField, onEditField, onDeleteField, onToggleRequired, onMoveField, dragSection, dragField }: {
  section: FormSection; index: number; count: number;
  onMoveSection: (to: number) => void; onEditSection: () => void; onDeleteSection: () => void;
  onAddField: () => void; onEditField: (f: FormField) => void; onDeleteField: (f: FormField) => void;
  onToggleRequired: (f: FormField) => void; onMoveField: (from: number, to: number) => void;
  dragSection: { start: () => void; drop: () => void };
  dragField: (fieldIndex: number) => { start: () => void; drop: () => void };
}) {
  const [open, setOpen] = useState(true);
  const n = section.fields.length;

  return (
    <div className="vf-section" onDragOver={(e) => e.preventDefault()} onDrop={dragSection.drop}
      style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div className="vf-section-head" draggable onDragStart={(e) => { if (e.target === e.currentTarget) dragSection.start(); else e.preventDefault(); }}
        style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px" }}>
        <span style={{ display: "flex", color: T.muted, cursor: "grab", flexShrink: 0 }} aria-hidden><GripVertical size={15} /></span>
        <ArrowPair label={section.title} canUp={index > 0} canDown={index < count - 1} onUp={() => onMoveSection(index - 1)} onDown={() => onMoveSection(index + 1)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: F, margin: 0, overflowWrap: "anywhere" }}>{section.title}</p>
          {section.description && <p style={{ fontSize: 12, color: T.sub, fontFamily: F, margin: "2px 0 0", overflowWrap: "anywhere" }}>{section.description}</p>}
        </div>
        <div className="vf-section-actions" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <Pill bg={T.s2} fg={T.muted} border={T.border}>{n} {n === 1 ? "field" : "fields"}</Pill>
          <button onClick={onEditSection} aria-label={`Edit ${section.title}`} style={iconBtn()}><Pencil size={13} /></button>
          <button onClick={onDeleteSection} aria-label={`Delete ${section.title}`} style={iconBtn(true)}><Trash2 size={13} /></button>
          <button onClick={() => setOpen((v) => !v)} aria-label={open ? "Collapse section" : "Expand section"} aria-expanded={open}
            style={{ display: "flex", background: "none", border: "none", cursor: "pointer", color: T.muted, padding: 4 }}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <>
          {section.fields.map((f, i) => (
            <FieldRow key={f.id} field={f} index={i} count={n} drag={dragField(i)}
              onToggleRequired={() => onToggleRequired(f)} onEdit={() => onEditField(f)} onDelete={() => onDeleteField(f)} onMove={(to) => onMoveField(i, to)} />
          ))}
          {n === 0 && (
            <p style={{ textAlign: "center", fontSize: 12, color: T.muted, fontFamily: F, padding: "28px 16px 8px", margin: 0, borderTop: `1px solid ${T.border}` }}>No fields in this section yet.</p>
          )}
          <div style={{ padding: "14px 20px 18px", display: "flex", justifyContent: n === 0 ? "center" : "flex-start", borderTop: n === 0 ? "none" : `1px solid ${T.border}` }}>
            <button onClick={onAddField} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.s2, color: T.text, fontSize: 12, fontFamily: F, fontWeight: 500, cursor: "pointer" }}>
              <Plus size={13} /> Add Field
            </button>
          </div>
        </>
      )}
    </div>
  );
}
